import React, {ReactNode} from "react";
import MenuButton from "../../shared/menu/MenuButton";
import portalConfiguration from "../../shared/configuration/PortalConfiguration";
import AdminContext from "../AdminContext";
import AbstractMenuBar from "../../shared/menu/AbstractMenuBar";
import {RestartDialogDefinition} from "../dialog/components/RestartDialog";
import {AdminGlobals} from "../Admin";
import {LicenseDialogDefinition} from "../dialog/components/LicenseDialog";
import {FunctionsDialogDefinition} from "../dialog/components/FunctionsDialog";
import {NewsDialogDefinition} from "../dialog/components/NewsDialog";
import {LocalAuthDialogDefinition} from "../dialog/components/LocalAuthDialog";
import {TsaDialogDefinition} from "../dialog/components/TsaDialog";
import {ProxyDialogDefinition} from "../dialog/components/ProxyDialog";
import {
	ApplicationConfigKeystore,
	ApplicationInterface,
	AuthProvider,
	BackgroundFileDataStore,
	ConfigurationType,
	FileGroupDataStore,
	Information,
	License,
	LogConfigurationMode,
	LogFileConfiguration,
	LogFileConfigurationInterface,
	LogoFileDataStore,
	NewsPanel,
	OAuth,
	Portal as PortalConfig,
	ProxiesApplication,
	ServerConfigLocalAuth,
	ServerConfigUserStorage,
	ServerInterface,
	ServerStatus,
	ThemeFileDataStore,
	TruststoreServer,
	UserCredentials,
	UserInterfaceResources,
	Users
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Portal from "../../portal/Portal";
import {changeLanguage} from "../../shared/translation/i18n";
import {UserSourceDialogDefinition} from "../dialog/components/UserSourceDialog";
import {UserDialogDefinition} from "../dialog/components/UserDialog";
import {DesignDialogDefinition} from "../dialog/components/DesignDialog";
import {BackgroundType, DesignType, LogoType, ThemeType} from "../../shared/models/DesignType";
import Administration from "../session/Administration";
import {KeystoreDialogDefinition} from "../dialog/components/KeystoreDialog";
import {TruststoreDialogDefinition} from "../dialog/components/TruststoreDialog";
import Exception from "../../shared/exception/Exception";
import ErrorCode from "../../shared/exception/ErrorCode";
import Translate from "../../shared/translation/Translate";
import emptyImage from "../../../resources/images/empty.png";
import {OAuthServerDialogDefinition} from "../dialog/components/OAuthServerDialog";
import {OAuthPortalDialogDefinition} from "../dialog/components/OAuthPortalDialog";
import {PortalAuthDialogDefinition} from "../dialog/components/PortalAuthDialog";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {I18nGlobals, Language} from "../../shared/translation/i18nInterface";
import {MenuItem} from "primereact/menuitem";
import {MessageLogGlobals} from "../../shared/log/MessageLogInterface";
import {PortalTsa} from "../../shared/models/PortalTsa";

enum MenuTabs {
	SERVER = "server",
	PORTAL = "portal",
}

interface AdminMenuBarProperties extends AdminGlobals, I18nGlobals, MessageLogGlobals {
	context: AdminContext;
	userId?: string;
}

interface AdminMenuBarState {
	activeTab: MenuTabs;
}

export class AdminMenuBarDefinition extends AbstractMenuBar<AdminMenuBarProperties, AdminMenuBarState> {
	/**
	 * Constructs a {@link AdminMenuBar} instance
	 */
	constructor(props: Readonly<AdminMenuBarProperties>) {
		super(props);

		this.state = {
			...this.state,
			activeTab: window.location.search.toLowerCase().includes(MenuTabs.PORTAL) ?
				MenuTabs.PORTAL : MenuTabs.SERVER
		};
	}

	private async loadDesignParameter(): Promise<DesignType> {
		let administration: Administration = this.props.context.session.getAdministration();

		let logoDataStore: LogoFileDataStore =
			await administration.getDatastore(FileGroupDataStore.Logo) as LogoFileDataStore;

		let logoType: LogoType = LogoType.DEFAULT;
		if (FunctionHelper.exists(logoDataStore.fileContent) && logoDataStore.fileContent !== "") {
			logoType = LogoType.CUSTOM;
		}

		let backgroundDataStore: BackgroundFileDataStore =
			await administration.getDatastore(FileGroupDataStore.Background) as BackgroundFileDataStore;

		let backgroundType: BackgroundType = BackgroundType.DEFAULT;
		if (
			FunctionHelper.exists(backgroundDataStore.fileContent) &&
			backgroundDataStore.fileContent === emptyImage.substring(emptyImage.indexOf(',') + 1)
		) {
			backgroundType = BackgroundType.HIDDEN;
		} else if (FunctionHelper.exists(backgroundDataStore.fileContent) && backgroundDataStore.fileContent !== "") {
			backgroundType = BackgroundType.CUSTOM;
		}

		let themeDataStore: ThemeFileDataStore =
			await administration.getDatastore(FileGroupDataStore.Theme) as ThemeFileDataStore;

		let themeType: ThemeType = ThemeType.DEFAULT;
		if (FunctionHelper.exists(themeDataStore.fileContent) && themeDataStore.fileContent !== "") {
			themeType = ThemeType.CUSTOM;

			for (let theme of Object.values(ThemeType)) {
				if (themeDataStore.fileContent!.indexOf("webpdf:theme " + theme) > -1) {
					themeType = theme as ThemeType;
					break;
				}
			}
		}

		return DesignType.fromJson({
			logoType: logoType,
			logo: logoDataStore.fileContent,
			themeType: themeType,
			theme: themeDataStore.fileContent,
			backgroundType: backgroundType,
			background: backgroundDataStore.fileContent,
			hideBackground: backgroundType === BackgroundType.HIDDEN
		} as DesignType);
	}

	/**
	 * Handles a exception and returns if the operation can be retried
	 */
	protected async onError(exception: Error): Promise<boolean> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.props.context.session.reconnectServer();
			return false;
		}

		switch (portalException.code) {
			case ErrorCode.WEBSERVICE_SESSION_TIMEOUT:
				try {
					await this.props.context.session.refresh();
					return true
				} catch (exception: any) {
					await this.onError(exception);
				}
				break;
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_FOUND:
			case ErrorCode.WEBSERVICE_SESSION_EXPIRED:
			case ErrorCode.WEBSERVICE_SESSION_UNABLE_TO_CREATE:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_ALLOWED:
			case ErrorCode.WEBSERVICE_SESSION_UNKNOWN_ERROR:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_PROVIDED:
				window.location.reload();
				break;
		}

		return false;
	}

	protected async handleDebugReset(logConfig?: LogFileConfigurationInterface): Promise<void> {
		let administration: Administration = this.props.context.session.getAdministration();
		let configuration = LogFileConfiguration.fromJson(logConfig);
		configuration.debugMode = LogConfigurationMode.None;

		try {
			await administration.updateConfiguration(ConfigurationType.Log, configuration);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.handleDebugReset(logConfig);
			}
			return;
		}
	}

	public renderMobileMenu(): ReactNode {
		let serverStatus: ServerStatus | undefined = ServerStatus.fromJson(this.props.serverStatus?.get());
		let portalInfo: Information = this.props.context.info;

		let logo: string = FunctionHelper.applyValue(
			portalInfo.userInterface?.resources?.logoFileName, UserInterfaceResources.getLogoFileNameDefault()
		);
		if (FunctionHelper.isEmpty(logo)) {
			logo = "logo" + portalConfiguration.cachingPath + ".png";
		}

		return (
			<div className="mobile-menu">
				<MenuButton {...this.props} icon="icon icon-menu"
							tooltip={Translate.value("mobile.label", {ns: "menu"})}
							menuClassName="mobile-menu-dropdown"
							menuItems={
								[
									{
										label: Translate.value("server.title", {ns: "menu"}),
										className: this.state.activeTab === MenuTabs.SERVER ? "active" : "",
										command: () => {
											this.setState({
												activeTab: MenuTabs.SERVER
											});
										}
									},
									{
										label: Translate.value("portal.title", {ns: "menu"}),
										className: this.state.activeTab === MenuTabs.PORTAL ? "active" : "",
										command: () => {
											this.setState({
												activeTab: MenuTabs.PORTAL
											});
										}
									}
								]
							}/>
				<div className="logo tooltip" data-pr-tooltip={
					serverStatus?.server?.serverVersion?.replace(/ *\([^)]*\) */g, "").trim()
				}>
					<img src={portalConfiguration.baseUrl + "image/res/" + logo} alt="webPDF Logo"
						 onClick={() => window.location.reload()}/>
				</div>
			</div>

		);
	}

	private renderToolbar(): ReactNode {
		let user: UserCredentials | undefined = this.props.context.user;
		let logConfig: LogFileConfigurationInterface | undefined = this.props.logConfig?.get();
		let provider: AuthProvider | undefined = this.getProvider();

		let userName: Array<MenuItem> = [];
		if (!FunctionHelper.isEmpty(user?.userName)) {
			userName.push({
				className: "legend",
				label: user!.userName
			});
		}

		let generalMenuItems: Array<MenuItem> = [];

		if (FunctionHelper.applyValue(user?.isAdmin, UserCredentials.getIsAdminDefault()) &&
			logConfig?.debugMode !== LogConfigurationMode.None) {
			generalMenuItems.push({
				icon: "icon icon-bug",
				label: Translate.value("toolbar.debug", {ns: "menu"}),
				command: () => this.handleDebugReset.bind(this)(logConfig)
			});
		}

		if (!FunctionHelper.applyValue(portalConfiguration.fixedAdminLanguage, false)) {
			generalMenuItems.push({
				icon: "icon icon-flag",
				label: Translate.value("toolbar.language.title", {ns: "menu"}),
				items: [
					{
						label: Translate.value(
							"toolbar.language.english", {ns: "menu"}
						),
						icon: "icon icon-flag-en",
						command: () => changeLanguage(Language.ENGLISH)
					},
					{
						label: Translate.value(
							"toolbar.language.german", {ns: "menu"}
						),
						icon: "icon icon-flag-de",
						command: () => changeLanguage(Language.GERMAN)
					}
				]
			});
		}

		return (
			<>
				<div className="toolbar mobile" data-help="global.menu.general" data-position="left">
					{FunctionHelper.exists(user) ?
						<MenuButton {...this.props} tooltipOptions={{position: 'left'}}
									tooltip={Translate.value("toolbar.user", {ns: "menu"})}
									className="usermanagement-button"
									menuItems={
										[
											...userName,
											{
												icon: "icon icon-home",
												label: Translate.value("toolbar.home", {ns: "menu"}),
												command: () => {
													window.location.href =
														portalConfiguration.portalUrl.toString() + Portal.route;
												}
											},
											{
												className: "global-help",
												icon: "icon icon-help circle",
												label: Translate.value("toolbar.help", {ns: "menu"}),
												command: (e) => {
													this.props.context.getPage().setState({
														showHelpScreen: true
													});
												}
											},
											...generalMenuItems,
											{
												separator: true
											},
											{
												label: Translate.value(
													"toolbar.logout", {ns: "menu"}
												),
												icon: "icon icon-logout",
												className: "global-user-logout",
												command: () => this.handleLogout.bind(this)()
											}
										]
									}>
							{this.renderUserIcon(provider)}
						</MenuButton> :
						<MenuButton {...this.props} icon="icon icon-user admin" className="global-user-button"
									tooltipOptions={{position: 'left'}}
									onClick={this.handleLogin.bind(this)}
									tooltip={Translate.value(
										"toolbar.login", {ns: "menu"}
									)}/>
					}
				</div>
				<div className="toolbar" data-help="global.menu.general" data-position="left">
					{FunctionHelper.applyValue(user?.isAdmin, UserCredentials.getIsAdminDefault()) &&
						logConfig?.debugMode !== LogConfigurationMode.None &&
						<MenuButton {...this.props} icon="icon icon-bug"
									tooltipOptions={{position: 'left'}}
									menuItems={
										[
											{
												label: Translate.value(
													"toolbar.debug", {ns: "menu"}
												),
												command: () => this.handleDebugReset.bind(this)(logConfig)
											}
										]
									}/>
					}
					{FunctionHelper.exists(user) ?
						<MenuButton {...this.props} tooltipOptions={{position: 'left'}} className="global-user-button"
									tooltip={Translate.value("toolbar.user", {ns: "menu"})}
									menuItems={
										[
											{
												label: Translate.value(
													"toolbar.logout", {ns: "menu"}
												),
												icon: "icon icon-logout",
												className: "global-user-logout",
												command: () => this.handleLogout.bind(this)()
											}
										]
									}>
							{this.renderUserIcon(provider)}
							<span className="p-button-label p-c">{user!.userName}</span>
						</MenuButton> :
						<MenuButton {...this.props} icon="icon icon-user admin"
									tooltipOptions={{position: 'left'}}
									onClick={this.handleLogin.bind(this)}
									tooltip={Translate.value(
										"toolbar.login", {ns: "menu"}
									)}/>
					}
					<MenuButton {...this.props} icon="icon icon-home"
								tooltipOptions={{position: 'left'}}
								tooltip={Translate.value("toolbar.home", {ns: "menu"})}
								onClick={() => {
									window.location.href = portalConfiguration.portalUrl.toString() + Portal.route;
								}}/>
					<MenuButton {...this.props} icon="icon icon-help circle" className="global-help-button"
								onClick={(e) => {
									if (e.ctrlKey && e.altKey) {
										this.props.context.getPage().setState({
											showDebugDialog: true
										});
										return;
									}

									this.props.context.getPage().setState({
										showHelpScreen: true
									});
								}}
								tooltipOptions={{position: 'left'}}
								tooltip={Translate.value("toolbar.help", {ns: "menu"})}/>
					{!FunctionHelper.applyValue(portalConfiguration.fixedAdminLanguage, false) &&
						<MenuButton {...this.props} icon="icon icon-flag" className="global-language-button"
									tooltipOptions={{position: 'left'}}
									tooltip={Translate.value(
										"toolbar.language.title", {ns: "menu"}
									)}
									menuItems={
										[
											{
												label: Translate.value(
													"toolbar.language.english", {ns: "menu"}
												),
												className: "global-language-en",
												icon: "icon icon-flag-en",
												command: () => changeLanguage(Language.ENGLISH)
											},
											{
												label: Translate.value(
													"toolbar.language.german", {ns: "menu"}
												),
												className: "global-language-de",
												icon: "icon icon-flag-de",
												command: () => changeLanguage(Language.GERMAN)
											}
										]
									}/>
					}
					<MenuButton {...this.props} tooltipOptions={{position: 'left'}} icon="icon icon-bell"
								className={(this.props.showLog ? " active" : "")}
								tooltip={Translate.value("toolbar.log", {ns: "menu"})}
								onClick={() => this.props.context.getPage().setState({
									showLog: !this.props.showLog
								})}/>
				</div>
			</>
		)
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let user: UserCredentials | undefined = this.props.context.user;
		let portalInfo: Information = this.props.context.info;
		let serverConfig: ServerInterface | undefined = this.props.serverConfig?.get();
		let applicationConfig: ApplicationInterface | undefined = this.props.applicationConfig?.get();

		return (
			<>
				<div className="menu-bar">
					<div className="tabs">
						{this.renderMobileMenu()}
						<ul>
							<li className={this.state.activeTab === MenuTabs.SERVER ? "active" : ""}>
								<MenuButton {...this.props} onClick={() => {
									this.setState({
										activeTab: MenuTabs.SERVER
									});
								}}>
									<Translate {...this.props} data={"server.title"} namespace="menu"/>
								</MenuButton>
							</li>
							<li className={this.state.activeTab === MenuTabs.PORTAL ? "active" : ""}>
								<MenuButton {...this.props} onClick={() => {
									this.setState({
										activeTab: MenuTabs.PORTAL
									});
								}}>
									<Translate {...this.props} data={"portal.title"} namespace="menu"/>
								</MenuButton>
							</li>
						</ul>
						{this.renderToolbar()}
					</div>
					<div className="tab-panels in" ref={this.resizeRoot}>
						{this.state.activeTab === MenuTabs.SERVER &&
							<div className="tab-panel">
								<div className="menu-block" ref={this.resizeContainer}
									 data-help="global.menu.admin.server" data-position="bottom">
									<div className="menu-group">
										{RestartDialogDefinition.isEnabled(portalInfo) &&
											<MenuButton {...this.props} icon="icon icon-restart"
														disabled={!RestartDialogDefinition.isActive(portalInfo, user)}
														label={Translate.value(
															"server.restart", {ns: "menu"}
														)}
														onClick={() => this.props.context.getPage().setState({
															showRestartDialog: true
														})}/>
										}
									</div>
									<div className="menu-separator p-md-hidden"/>
									<div className="menu-group">
										<MenuButton {...this.props} icon="icon icon-proxy"
													disabled={!ProxyDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value("server.proxy", {ns: "menu"})}
													onClick={() => this.props.context.getPage().setState({
														showProxyDialog: true,
														proxyParameter: ProxiesApplication.fromJson(
															FunctionHelper.applyValue(
																applicationConfig?.proxies, {}
															)
														)
													})}/>
										<MenuButton {...this.props} icon="icon icon-oauth"
													disabled={!OAuthServerDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value("server.oauth", {ns: "menu"})}
													onClick={() => this.props.context.getPage().setState({
														showOAuthServerDialog: true,
														oAuthServerParameter: OAuth.fromJson(
															FunctionHelper.applyValue(
																serverConfig?.oauth, {}
															)
														)
													})}/>
									</div>
									<div className="menu-separator p-md-hidden"/>
									<div className="menu-group">
										<MenuButton {...this.props} icon="icon icon-key"
													disabled={!LicenseDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"server.license", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showLicenseDialog: true,
														licenseParameter: License.fromJson(
															FunctionHelper.applyValue(
																applicationConfig?.license, {}
															)
														)
													})}/>
										<MenuButton {...this.props} icon="icon icon-keystore"
													disabled={!KeystoreDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"server.keystore", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showKeystoreDialog: true,
														keystoreParameter: ApplicationConfigKeystore.fromJson(
															FunctionHelper.applyValue(
																applicationConfig?.keystore, {}
															)
														)
													})}/>
										<MenuButton {...this.props} icon="icon icon-certificate"
													disabled={!TruststoreDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"server.truststore", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showTruststoreDialog: true,
														truststoreParameter: TruststoreServer.fromJson(
															FunctionHelper.applyValue(
																serverConfig?.truststore, {}
															)
														)
													})}/>
									</div>
									<div className="menu-group">
										<MenuButton {...this.props} icon="icon icon-clock"
													disabled={!TsaDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"server.tsa", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showTsaDialog: true,
														tsaParameter: PortalTsa.fromJson(
															FunctionHelper.applyValue(applicationConfig?.tsa, {})
														)
													})}/>
										<MenuButton {...this.props} icon="icon icon-shield"
													disabled={!LocalAuthDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"server.local_auth", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showLocalAuthDialog: true,
														LocalAuthParameter: ServerConfigLocalAuth.fromJson(
															FunctionHelper.applyValue(
																serverConfig?.localAuth, {}
															)
														)
													})}/>
									</div>
									<p className="menu-group-label">
										<Translate {...this.props} data="server.title" namespace="menu"/>
									</p>
								</div>
							</div>
						}
						{this.state.activeTab === MenuTabs.PORTAL &&
							<div className="tab-panel">
								<div className="menu-block" ref={this.resizeContainer}
									 data-help="global.menu.admin.server" data-position="bottom">
									<div className="menu-group">
										<MenuButton {...this.props} icon="icon icon-news"
													disabled={!NewsDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"portal.news", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showNewsDialog: true,
														newsParameter: NewsPanel.fromJson(
															FunctionHelper.applyValue(
																applicationConfig?.portal.newsPanel, {}
															)
														)
													})}/>
										{FunctionHelper.exists(serverConfig?.user.xml) &&
											<MenuButton {...this.props} icon="icon icon-user multiple"
														disabled={!UserDialogDefinition.isActive(portalInfo, user)}
														label={Translate.value(
															"portal.user", {ns: "menu"}
														)}
														onClick={() => this.props.context.getPage().setState({
															showUserDialog: true,
															userParameter: Users.fromJson(
																FunctionHelper.applyValue(
																	this.props.userConfig?.get(), {}
																)
															)
														})}/>
										}
										<MenuButton {...this.props} icon="icon icon-user source"
													disabled={!UserSourceDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"portal.user_source", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showUserSourceDialog: true,
														userSourceParameter: ServerConfigUserStorage.fromJson(
															FunctionHelper.applyValue(
																serverConfig?.user, {}
															)
														)
													})}/>
									</div>
									<div className="menu-group">
										<MenuButton {...this.props} icon="icon icon-oauth"
													disabled={!OAuthPortalDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value("portal.oauth", {ns: "menu"})}
													onClick={() => this.props.context.getPage().setState({
														showOAuthPortalDialog: true,
														oAuthPortalParameter: OAuth.fromJson(
															FunctionHelper.applyValue(
																serverConfig?.oauth, {}
															)
														)
													})}/>
										<MenuButton {...this.props} icon="icon icon-functions"
													disabled={!FunctionsDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"portal.functions", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showFunctionsDialog: true,
														functionsParameter: PortalConfig.fromJson(
															FunctionHelper.applyValue(
																applicationConfig?.portal, {}
															)
														)
													})}/>
										<MenuButton {...this.props} icon="icon icon-design"
													disabled={!DesignDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"portal.design", {ns: "menu"}
													)}
													onClick={async () => {
														this.props.context.getPage().setState({
															showDesignDialog: true,
															designParameter: await this.loadDesignParameter()
														})
													}}/>
									</div>
									<div className="menu-group">
										<MenuButton {...this.props} icon="icon icon-shield"
													disabled={!PortalAuthDialogDefinition.isActive(portalInfo, user)}
													label={Translate.value(
														"portal.portal_auth", {ns: "menu"}
													)}
													onClick={() => this.props.context.getPage().setState({
														showPortalAuthDialog: true,
														portalAuthParameter: PortalConfig.fromJson(
															FunctionHelper.applyValue(
																applicationConfig?.portal, {}
															)
														)
													})}/>
									</div>
									<p className="menu-group-label">
										<Translate {...this.props} data="portal.title" namespace="menu"/>
									</p>
								</div>
							</div>
						}
					</div>
				</div>
			</>
		);
	}
}

const AdminMenuBar = React.memo(AdminMenuBarDefinition,
	(prevProps: Readonly<AdminMenuBarProperties>, nextProps: Readonly<AdminMenuBarProperties>) => {
		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.showLog !== prevProps.showLog) {
			return false;
		}

		if (nextProps.userId !== prevProps.userId) {
			return false;
		}

		if (prevProps.serverStatus !== nextProps.serverStatus) {
			return false;
		}

		if (!prevProps.applicationConfig?.equals(nextProps.applicationConfig)) {
			return false;
		}

		if (!prevProps.serverConfig?.equals(nextProps.serverConfig)) {
			return false;
		}

		if (!prevProps.userConfig?.equals(nextProps.userConfig)) {
			return false;
		}

		if (!prevProps.logConfig?.equals(nextProps.logConfig)) {
			return false;
		}

		return true;
	}
);

export default AdminMenuBar;